import {
  ActionReducerMapBuilder,
  AsyncThunk,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import _ from "lodash";

import { IQuery } from "containers/lists/utils";
import { updateErrorsList } from "utils/reducerUtils";

import { formatTerritoryForFE } from "../utils/formatTerritory";
import { ITerritoryState, territoryInitialState } from "./sliceTypes";
import {
  createTerritories,
  deleteTerritories,
  editTerritories,
  editTerritoriesSchema,
  fetchTerritories,
  fetchTerritoriesSchema,
  unassignTerritoryOwners,
} from "./territoriesThunks";

type TLoadingKey =
  | "isFetchingTerritoriesItems"
  | "isFetchingTerritorySchema"
  | "isCreatingTerritoriesItems"
  | "isUpdatingTerritoriesItems"
  | "isUpdatingTerritorySchema"
  | "isDeletingTerritoriesItems"
  | "isUnassigningTerritoriesItems";

const recordError = (
  state: ITerritoryState,
  action: { type: string; payload?: unknown; error?: unknown },
) => {
  state.errors = updateErrorsList(state, {
    type: action.type,
    error: action.payload ?? action.error,
  });
};

const trackRequest = <Returned>(
  builder: ActionReducerMapBuilder<ITerritoryState>,
  thunk: AsyncThunk<Returned, any, any>,
  loadingKey: TLoadingKey,
  onSuccess?: (state: ITerritoryState, payload: Returned) => void,
) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[loadingKey] = true;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      onSuccess?.(state, action.payload);
      state[loadingKey] = false;
    })
    .addCase(thunk.rejected, (state, action) => {
      recordError(state, action);
      state[loadingKey] = false;
    });
};

const territoriesSlice = createSlice({
  name: "territories",
  initialState: territoryInitialState,
  reducers: {
    setFilterTerritoriesQuery: (state, action: PayloadAction<IQuery>) => {
      state.filterQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    trackRequest(
      builder,
      fetchTerritories,
      "isFetchingTerritoriesItems",
      (state, payload) => {
        state.territories = payload.territories;
        state.item_count = payload.item_count;
        state.hasMoreTerritories = payload.has_more;
        state.filterQuery = payload.frontendFilters ?? {};
      },
    );
    trackRequest(
      builder,
      fetchTerritoriesSchema,
      "isFetchingTerritorySchema",
      (state, schema) => {
        state.territoriesSchema = schema;
      },
    );
    trackRequest(
      builder,
      createTerritories,
      "isCreatingTerritoriesItems",
      (state, created) => {
        state.territories = [...state.territories, ...created];
      },
    );
    trackRequest(
      builder,
      editTerritories,
      "isUpdatingTerritoriesItems",
      (state, updated) => {
        state.territories = state.territories.map((territory) => {
          const next = _.find(updated, { id: territory.id });
          return next ? (formatTerritoryForFE(next) ?? territory) : territory;
        });
      },
    );
    trackRequest(
      builder,
      editTerritoriesSchema,
      "isUpdatingTerritorySchema",
      (state, schema) => {
        state.territoriesSchema = schema;
      },
    );
    trackRequest(builder, deleteTerritories, "isDeletingTerritoriesItems");
    trackRequest(
      builder,
      unassignTerritoryOwners,
      "isUnassigningTerritoriesItems",
    );
  },
});

export const { setFilterTerritoriesQuery } = territoriesSlice.actions;

export default territoriesSlice.reducer;
